import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import UsersPanel from "@/pages/admin/UsersPanel";
import OffersPanel from "@/pages/admin/OffersPanel";
import api, { formatApiErrorDetail } from "@/lib/api";

function useAdminList(endpoint) {
  const [data, setData] = useState(null);
  const reload = () => api.get(endpoint).then(({ data }) => setData(data));
  useEffect(() => { reload(); }, [endpoint]);
  return [data, reload];
}

function PaymentsPanel() {
  const [payments] = useAdminList("/admin/payments");
  if (!payments) return <Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7] mx-auto" />;
  return (
    <div className="space-y-2" data-testid="admin-payments-panel">
      {payments.map((p) => (
        <div key={p.id} className="flex items-center justify-between bg-white rounded-xl border border-black/5 p-4 text-sm" data-testid={`admin-payment-row-${p.id}`}>
          <span>{p.type} &middot; {p.user_id}</span>
          <div className="flex items-center gap-2">
            <span className="font-semibold">{p.amount}&euro;</span>
            <Badge variant="outline">{p.status}</Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

function DocumentsPanel() {
  const { t } = useTranslation();
  const [pending, reload] = useAdminList("/admin/documents/pending");
  const validate = async (candidateId, storagePath) => {
    await api.post(`/admin/documents/${candidateId}/validate?storage_path=${encodeURIComponent(storagePath)}`);
    reload();
  };
  if (!pending) return <Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7] mx-auto" />;
  return (
    <div className="space-y-2" data-testid="admin-documents-panel">
      {pending.length === 0 && <p className="text-center text-[#2B2B2B]/50 py-8">{t("search.noResults")}</p>}
      {pending.map((p) => (
        <div key={p.candidate_id} className="bg-white rounded-xl border border-black/5 p-4 text-sm" data-testid={`admin-pending-doc-${p.candidate_id}`}>
          <p className="font-semibold mb-2">{p.first_name} {p.last_name}</p>
          {p.documents.map((d) => (
            <div key={d.storage_path} className="flex items-center justify-between py-1">
              <span>{d.type} &middot; {d.filename}</span>
              <Button size="sm" variant="outline" onClick={() => validate(p.candidate_id, d.storage_path)} data-testid={`admin-validate-doc-${d.storage_path}`}>
                {t("admin.validateDocument")}
              </Button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function StatsPanel() {
  const { t } = useTranslation();
  const [stats] = useAdminList("/admin/stats");
  if (!stats) return <Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7] mx-auto" />;
  const items = [
    ["totalUsers", stats.total_users], ["totalProducers", stats.total_producers],
    ["totalCandidates", stats.total_candidates], ["activeOffers", stats.active_offers],
    ["totalApplications", stats.total_applications], ["totalUnlocks", stats.total_unlocks],
    ["totalRevenue", `${stats.total_revenue}€`],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="admin-stats-panel">
      {items.map(([key, value]) => (
        <div key={key} className="bg-white rounded-2xl border border-black/5 p-5 text-center">
          <p className="font-heading text-2xl font-bold text-[#4ba3b7]">{value}</p>
          <p className="text-xs text-[#2B2B2B]/50 mt-1">{t(`admin.${key}`)}</p>
        </div>
      ))}
    </div>
  );
}

function BlogPanel() {
  const { t } = useTranslation();
  const [posts, reload] = useAdminList("/blog/admin/all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", post_type: "article", published: true });
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditingId(null);
    setForm({ title: "", content: "", post_type: "article", published: true });
    setDialogOpen(true);
  };

  const openEdit = (post) => {
    setEditingId(post.id);
    setForm({ title: post.title, content: post.content, post_type: post.post_type, published: post.published });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = editingId ? await api.put(`/blog/admin/${editingId}`, form) : await api.post("/blog/admin", form);
      if (data.languages_translated && data.languages_translated.length < 8) {
        toast.warning(t("blog.partialTranslationWarning", { count: data.languages_translated.length }));
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/blog/admin/${id}`);
    reload();
  };

  if (!posts) return <Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7] mx-auto" />;

  return (
    <div data-testid="admin-blog-panel">
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <div className="flex justify-end mb-4">
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] gap-2" data-testid="admin-blog-new-post-button">
              <Plus className="h-4 w-4" /> {t("blog.newPost")}
            </Button>
          </DialogTrigger>
        </div>
        <DialogContent className="rounded-2xl max-w-xl">
          <DialogHeader><DialogTitle>{editingId ? t("blog.editPost") : t("blog.newPost")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("blog.postTitle")}
                   className="h-11 rounded-xl" data-testid="admin-blog-title-input" />
            <Select value={form.post_type} onValueChange={(v) => setForm({ ...form, post_type: v })}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="admin-blog-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="article">{t("blog.articles")}</SelectItem>
                <SelectItem value="faq">{t("blog.faq")}</SelectItem>
              </SelectContent>
            </Select>
            <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder={t("blog.postContent")}
                      className="rounded-xl min-h-[160px]" data-testid="admin-blog-content-input" />
            <p className="text-xs text-[#2B2B2B]/50" data-testid="admin-blog-markdown-hint">{t("blog.markdownHint")}</p>
            <p className="text-xs text-[#2B2B2B]/50" data-testid="admin-blog-translation-hint">{t("blog.autoTranslateHint")}</p>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving || !form.title.trim() || !form.content.trim()} className="bg-[#4ba3b7] hover:bg-[#3D8A9A] rounded-xl" data-testid="admin-blog-save-button">
              {saving ? t("blog.translating") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center justify-between bg-white rounded-xl border border-black/5 p-4 text-sm" data-testid={`admin-blog-row-${p.id}`}>
            <div>
              <p className="font-semibold">{p.title}</p>
              <Badge variant="outline" className="text-xs mt-1">{p.post_type}</Badge>
              {!p.published && <Badge variant="outline" className="text-xs mt-1 ml-1 text-[#FF3B30]">{t("blog.unpublished")}</Badge>}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`admin-blog-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-[#FF3B30]" onClick={() => remove(p.id)} data-testid={`admin-blog-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { t } = useTranslation();
  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="admin-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-8">{t("admin.adminPanel")}</h1>
      <Tabs defaultValue="stats">
        <TabsList className="mb-8 flex-wrap h-auto rounded-xl">
          <TabsTrigger value="stats" data-testid="admin-tab-stats">{t("admin.tabStats")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">{t("admin.tabUsers")}</TabsTrigger>
          <TabsTrigger value="offers" data-testid="admin-tab-offers">{t("admin.tabOffersAdmin")}</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">{t("admin.tabPaymentsAdmin")}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="admin-tab-documents">{t("admin.tabDocumentsAdmin")}</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-tab-blog">{t("blog.title")}</TabsTrigger>
        </TabsList>
        <TabsContent value="stats"><StatsPanel /></TabsContent>
        <TabsContent value="users"><UsersPanel /></TabsContent>
        <TabsContent value="offers"><OffersPanel /></TabsContent>
        <TabsContent value="payments"><PaymentsPanel /></TabsContent>
        <TabsContent value="documents"><DocumentsPanel /></TabsContent>
        <TabsContent value="blog"><BlogPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
